"use client";

import { usePublicClient } from "wagmi";

import { getChainConfig } from "@/config/chains/helpers";

import { useOnChange } from "@/lib/hooks/use-on-change";
import { dispatchAgoraEvent } from "@/lib/utils/dispatch-agora-event";

import { useBasicChainStatus } from "./use-basic-chain-status";
import { useChainFromUrlParam } from "./use-chain-from-url-param";
import { useWalletCore } from "./use-wallet-core";

export function useEvmEnvironment() {
  //
  // ─────────────────────────────────────────────────────────────
  // Chain Context (Shared across Swap + CrossChain)
  // ─────────────────────────────────────────────────────────────
  //
  // Uses `?chainId=` from the URL as the source chain ID.
  // Synchronizes this with Wagmi, and exposes metadata:
  // - name, config, icon, factory address
  // - current + previous chainId
  // - helper to update the chainId in the URL
  //

  const { chainConfig, chainFactoryProxyAddress, chainIconUrl, chainId, chainName, updateChainId } =
    useChainFromUrlParam();

  const publicClient = usePublicClient({ chainId });

  const chainStatus = useBasicChainStatus(chainId);

  //
  // ─────────────────────────────────────────────────────────────
  // Wallet Context (Connected Wallet on Source Chain)
  // ─────────────────────────────────────────────────────────────
  //
  // Uses the active `chainId` to fetch:
  // - user's wallet address
  // - native balance (for gas)
  // - truncated address and emoji avatar for UI
  //

  const {
    invalidateNativeBalanceQuery,
    isWalletConnected,
    isWalletGasLoading,
    nativeBalanceQuery,
    walletAddress,
    walletAddressTruncated,
    walletGasBalance,
    walletGasStatus,
    walletThemeColor,
    walletThemeEmoji,
  } = useWalletCore({ chainId });

  const sharedEventDetails = {
    chainId,
    chainName,
    chainStatus,
    isWalletConnected,
    vmType: "evm",
    walletAddress,
    walletThemeColor,
    walletThemeEmoji,
  };

  // wallet either connected or disconnected
  useOnChange(isWalletConnected, (_prev, next) => {
    const changedToConnected = next === true;

    dispatchAgoraEvent({
      eventLocation: "global",
      eventType: "product.wallet.updated",
      eventAction: changedToConnected ? "wallet.connected" : "wallet.disconnected",
      eventDetails: {
        ...sharedEventDetails,
      },
    });
  });

  // the chain switched
  useOnChange(chainId, (prevChain, nextChain) => {
    const prevChainConfig = getChainConfig(prevChain);
    const nextChainConfig = getChainConfig(nextChain);

    dispatchAgoraEvent({
      eventLocation: "global",
      eventType: "product.wallet.updated",
      eventAction: "wallet.chain-updated",
      eventDetails: {
        ...sharedEventDetails,
        prevChainId: prevChainConfig?.id,
        prevChainName: prevChainConfig?.name,
        nextChainId: nextChainConfig?.id,
        nextChainName: nextChainConfig?.name,
      },
    });
  });

  return {
    chainConfig,
    chainFactoryProxyAddress,
    chainIconUrl,
    // from useChain
    chainId,
    chainName,
    // useBasicChainStatus
    chainStatus,

    // from useWalletCore
    invalidateNativeBalanceQuery,

    isWalletConnected,

    isWalletGasLoading,
    nativeBalanceQuery,
    // usePublicClient
    publicClient,
    updateChainId,
    walletAddress,
    walletAddressTruncated,
    walletGasBalance,
    walletGasStatus,
    walletThemeColor,
    walletThemeEmoji,
  };
}
